import { API_ENDPOINTS } from '@tsgecart/config';
import { storefrontApiFetch } from '../../../../lib/api/storefront-client.js';
import type { AddCartItemInput, UpdateCartItemInput, CartView } from '../types/cart.js';

/** Header carrying a guest cart's token -- never a cookie, matching the backend's `apps/worker/src/routes/v1/commerce/cart.ts`. */
const GUEST_CART_TOKEN_HEADER = 'X-Guest-Cart-Token';

/**
 * Exactly one of `token` (signed-in, Clerk Bearer token) or `guestToken`
 * (signed-out, this browser tab's guest cart session) is expected per
 * call -- callers resolve which one applies (see `use-cart.ts`), this
 * module just forwards whichever identity it's given.
 */
export interface FetchOptions {
  token?: string | null;
  guestToken?: string | null;
  signal?: AbortSignal;
}

function identityHeaders(options: FetchOptions): Record<string, string> {
  return options.guestToken ? { [GUEST_CART_TOKEN_HEADER]: options.guestToken } : {};
}

export async function fetchCart(options: FetchOptions): Promise<CartView> {
  return storefrontApiFetch<CartView>(API_ENDPOINTS.cart, { ...options, headers: identityHeaders(options) });
}

export async function addCartItem(input: AddCartItemInput, options: FetchOptions): Promise<CartView> {
  return storefrontApiFetch<CartView>(`${API_ENDPOINTS.cart}/items`, {
    ...options,
    method: 'POST',
    body: input,
    headers: identityHeaders(options),
  });
}

export async function updateCartItem(itemId: string, input: UpdateCartItemInput, options: FetchOptions): Promise<CartView> {
  return storefrontApiFetch<CartView>(`${API_ENDPOINTS.cart}/items/${itemId}`, {
    ...options,
    method: 'PATCH',
    body: input,
    headers: identityHeaders(options),
  });
}

export async function removeCartItem(itemId: string, options: FetchOptions): Promise<CartView> {
  return storefrontApiFetch<CartView>(`${API_ENDPOINTS.cart}/items/${itemId}`, {
    ...options,
    method: 'DELETE',
    headers: identityHeaders(options),
  });
}

export async function clearCart(options: FetchOptions): Promise<CartView> {
  return storefrontApiFetch<CartView>(API_ENDPOINTS.cart, { ...options, method: 'DELETE', headers: identityHeaders(options) });
}

/** Creates a brand-new guest cart session (public, no auth). Only ever called lazily, on a signed-out user's first cart mutation -- never eagerly on page load. */
export async function createGuestCartSession(): Promise<{ guestToken: string }> {
  return storefrontApiFetch<{ guestToken: string }>(`${API_ENDPOINTS.cart}/guest-session`, { method: 'POST' });
}

/** Merges a guest cart into the now-signed-in caller's own cart. Authenticated -- `token` is required. */
export async function mergeGuestCart(guestToken: string, options: { token: string }): Promise<CartView> {
  return storefrontApiFetch<CartView>(`${API_ENDPOINTS.cart}/merge`, {
    ...options,
    method: 'POST',
    body: { guestToken },
  });
}
